import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'

const NEW_XATA_URL = 'https://x.ata.network'
const COUNTDOWN_SECONDS = 5
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&display=swap'

// --- Animations ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0%, 100% { opacity: 0.12; transform: scale(1); }
  50% { opacity: 0.2; transform: scale(1.05); }
`

// --- Layout ---

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    145deg,
    #111c1c 0%,
    #1a2e2d 25%,
    #243d3a 45%,
    #2d4f4a 60%,
    #1e3331 80%,
    #111c1c 100%
  );
  font-family: 'Work Sans', sans-serif;
  overflow: hidden;
  z-index: 9999;
`

const GlowOrb = styled.div`
  position: absolute;
  top: -15%;
  right: 5%;
  width: 55%;
  height: 65%;
  background: radial-gradient(ellipse at center, rgba(120, 210, 190, 0.14) 0%, transparent 65%);
  pointer-events: none;
  animation: ${pulse} 6s ease-in-out infinite;
`

const GlowOrb2 = styled.div`
  position: absolute;
  bottom: -10%;
  left: -5%;
  width: 45%;
  height: 50%;
  background: radial-gradient(ellipse at center, rgba(100, 180, 170, 0.08) 0%, transparent 60%);
  pointer-events: none;
  animation: ${pulse} 8s ease-in-out infinite 2s;
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 24px;
  max-width: 600px;
  animation: ${fadeInUp} 0.8s ease-out;
`

// --- Logo ---

const Logo = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 6px;
  margin-bottom: 48px;
`

// --- Typography ---

const Heading = styled.h1`
  font-size: 52px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 16px;
  line-height: 1.1;
  letter-spacing: -1.5px;

  @media (max-width: 600px) {
    font-size: 34px;
    letter-spacing: -1px;
  }
`

const Subtitle = styled.p`
  font-size: 17px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 40px;
  line-height: 1.6;
  max-width: 460px;

  @media (max-width: 600px) {
    font-size: 15px;
    margin-bottom: 32px;
  }
`

// --- CTA with circular countdown ---

const borderShrink = keyframes`
  from {
    clip-path: inset(0 0 0 0 round 100px);
  }
  to {
    clip-path: inset(0 100% 0 0 round 100px);
  }
`

const CTAWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
`

const CTARing = styled.div<{ duration: number; paused: boolean }>`
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border-radius: 100px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: ${borderShrink} ${({ duration }) => duration}s linear forwards;
  animation-play-state: ${({ paused }) => (paused ? 'paused' : 'running')};
  pointer-events: none;
`

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 36px;
  background: #ffffff;
  color: #1a2a2a;
  border: none;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Work Sans', sans-serif;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  position: relative;
  z-index: 1;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }
`

const CountdownLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 4px;
`

// --- Feature pills ---

const PillRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`

const Pill = styled.div`
  padding: 6px 16px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
`

// --- Legacy link ---

const LegacyLink = styled.button`
  position: absolute;
  bottom: 36px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  font-family: 'Work Sans', sans-serif;
  cursor: pointer;
  padding: 8px 16px;
  transition: color 0.2s;
  z-index: 1;
  text-decoration: none;

  &:hover {
    color: rgba(255, 255, 255, 0.65);
    text-decoration: underline;
  }
`

// --- Component ---

export default function Landing() {
  const history = useHistory()
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const [paused, setPaused] = useState(false)
  const redirected = useRef(false)

  // Load Work Sans font
  useEffect(() => {
    const existing = document.querySelector(`link[href="${FONT_URL}"]`)
    if (!existing) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = FONT_URL
      document.head.appendChild(link)
    }
  }, [])

  // Countdown timer
  useEffect(() => {
    if (paused || redirected.current) return
    if (countdown <= 0) {
      redirected.current = true
      window.location.href = NEW_XATA_URL
      return
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown, paused])

  const handleLegacyClick = useCallback(() => {
    setPaused(true)
    history.push('/legacy')
  }, [history])

  return (
    <Wrapper>
      <GlowOrb />
      <GlowOrb2 />

      <Content>
        <Logo>XATA</Logo>

        <Heading>We&apos;ve evolved.</Heading>

        <Subtitle>
          XATA has a new home. Trade perpetuals with agentic execution across Hyperliquid, Aster,
          and Lighter — all from one interface.
        </Subtitle>

        <CTAWrapper>
          <CTARing duration={COUNTDOWN_SECONDS} paused={paused} />
          <CTAButton href={NEW_XATA_URL}>
            Explore the new XATA
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CTAButton>
        </CTAWrapper>

        <CountdownLabel>
          {paused ? 'Redirect paused' : `Redirecting in ${countdown}s`}
        </CountdownLabel>

        <PillRow>
          <Pill>Perp Trading</Pill>
          <Pill>Multi-venue</Pill>
          <Pill>Agentic Execution</Pill>
        </PillRow>
      </Content>

      <LegacyLink onClick={handleLegacyClick}>
        Need to withdraw funds? Access the legacy interface &rarr;
      </LegacyLink>
    </Wrapper>
  )
}
